import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { ArrowUp, ArrowDown } from 'lucide-react';

export const ScrollButton: React.FC = () => {
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const threshold = 50;

    const update = () => {
      const doc = document.documentElement;
      const y = window.scrollY || doc.scrollTop || 0;
      const scrollHeight = doc.scrollHeight || document.body.scrollHeight || 0;
      const innerH = window.innerHeight || 0;
      const max = Math.max(0, scrollHeight - innerH);

      const isTop = y <= threshold;
      const isBottom = y + innerH >= scrollHeight - threshold;

      setAtTop(isTop);
      setAtBottom(isBottom);

  // Visible when the page is scrollable and user is not at the very top.
  // This makes the button easier to find: it appears after any scroll on long pages
  // and stays visible at the bottom so users can quickly return to top.
  setVisible(max > 0 && !isTop);
      rafRef.current = null;
    };

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(update);
    };

    const onResize = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(update);
    };

  // initialize
  update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handleClick = () => {
    // If at top, scroll to bottom. Otherwise scroll to top.
    if (atTop) {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Choose icon: when at top show down arrow; otherwise show up arrow.
  const showDown = atTop && !atBottom;

  // Allow forcing the button visible for debugging via URL or localStorage
  const forceVisibleFromUrl = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('showScroll') === '1';
  const forceVisibleFromStorage = typeof window !== 'undefined' && window.localStorage?.getItem('forceScrollVisible') === '1';
  const forceVisible = forceVisibleFromUrl || forceVisibleFromStorage;

  return (
    <div>
      {/* Small floating button in bottom-right */}
      <div
        style={{ zIndex: 99999 }}
        className={`fixed right-4 bottom-24 transition-all duration-200 ease-in-out transform ${forceVisible || visible ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none translate-y-2'}`}>
        <Button onClick={handleClick} size="sm" variant="outline" className="h-10 w-10 p-0">
          {showDown ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default ScrollButton;
